import * as React from 'react';
import { IConditionError } from '../../../../../interface/conditions.interface';
import {
    Question,
    AddCondition,
    ConditionType,
    PreCondition,
    PostCondition,
    CONDITION_TYPES,
    ConditionRule,
    QuestionID
} from '@nexxt/common/types';
import { defaultRules } from '../default';
import AddPrePostCondition from './AddPrePostCondition';
import { SelectCondtionType } from '../ConditionCommon';
import { getConfirmationBox } from '../../../../common/swal/Sweetalert';
import { QuestionSummary } from '@nexxt/common/types';
import { alertMessage } from '../../../../../constants/alertMessage';
import { IToastNotification } from '../../../../../HOC/Notifications';
import { INCA_VARIABLE_FUNCTION_TYPES } from '@nexxt/common/incaLogic/types';

export interface AddConditionProps {
    toastNotification: IToastNotification;
    addCondition: AddCondition | null;
    editRuleIndex: number;
    questions: { [key: number]: QuestionSummary };
    selectConditionType: (type: ConditionType) => void;
    onCancelCondition: () => void;
    onSaveAddCondition: () => void;
    editRule: (ruleIndex: number) => void;
    updateCondition: (
        type: ConditionType,
        conditions: PreCondition | PostCondition,
        options?: any
    ) => void;
    getQuestionById: (questionId: number, options) => Promise<any>;
    filteredQuestionSummaries: QuestionSummary[];
    flattenQuestionSummaries: QuestionSummary[];
    errorObject: IConditionError;
    onCancelAddRule: () => void;
    editEnabled: boolean;
    currentQuestionId: QuestionID;
}

class AddConditionContainer extends React.Component<
    AddConditionProps,
    unknown
> {
    copyCondition = (addCondition: AddCondition) => {
        if (addCondition) {
            const newAddCondition: AddCondition = {
                ...addCondition,
                conditions: {
                    ...addCondition.conditions,
                    rules: (addCondition.conditions.rules || []).map((r) => ({
                        ...r
                    }))
                }
            };
            return newAddCondition;
        }
        return addCondition;
    };

    updateRuleReferenceQuestion = (index) => (value) => {
        const addCondition = this.copyCondition(this.props.addCondition);
        let currentRule: ConditionRule = null;
        if (addCondition.conditions) {
            currentRule = { ...addCondition.conditions.rules[index] };
            currentRule.selectedReferenceQ = {
                id: value.id,
                label: value.label || value.title,
                value: value.value || value.id
            };

            // Clear  the selected option value
            currentRule.compareOptions = [];
            currentRule.compareNumber = null;

            addCondition.conditions.rules.splice(index, 1, currentRule);
        }
        const questionId = value.id;
        if (questionId !== -1) {
            this.props
                .getQuestionById(questionId, { logicList: true })
                .then((response: Question) => {
                    if (currentRule) {
                        currentRule.compareValueOptions =
                            (response && response.options) || [];
                        addCondition.conditions.rules.splice(
                            index,
                            1,
                            currentRule
                        );
                        this.props.updateCondition(
                            addCondition.type,
                            addCondition.conditions
                        );
                    }
                });
        } else {
            /**  For custom questions  */
        }
        this.props.updateCondition(addCondition.type, addCondition.conditions);
    };

    updateRuleOperator = (index: number) => (value) => {
        const addCondition: AddCondition = this.copyCondition(
            this.props.addCondition
        );
        if (addCondition.conditions) {
            const currentRule = { ...addCondition.conditions.rules[index] };
            currentRule.selectedOperator = value;
            if (
                [
                    INCA_VARIABLE_FUNCTION_TYPES.IS_DISPLAYED,
                    INCA_VARIABLE_FUNCTION_TYPES.IS_NOT_DISPLAYED
                ].includes(value?.type)
            ) {
                currentRule.compareType = CONDITION_TYPES.CUSTOM_VALUE;
            }
            addCondition.conditions.rules.splice(index, 1, currentRule);
        }
        this.props.updateCondition(addCondition.type, addCondition.conditions);
    };

    updateRuleValue =
        (index) =>
        (
            type: 'options' | 'number' | 'string' | 'boolean',
            value,
            compareType?: CONDITION_TYPES
        ) => {
            const addCondition = this.copyCondition(this.props.addCondition);
            if (addCondition.conditions) {
                const currentRule = { ...addCondition.conditions.rules[index] };
                switch (type) {
                    case 'options':
                        currentRule.compareOptions = value;
                        currentRule.compareType = compareType;
                        break;
                    case 'number':
                        currentRule.compareNumber = value;
                        currentRule.compareType = compareType;
                        break;
                    case 'string':
                        currentRule.compareCustomValue = value;
                        currentRule.compareType = compareType;
                        break;
                    case 'boolean':
                        currentRule.compareType = compareType;
                        break;
                }

                addCondition.conditions.rules.splice(index, 1, currentRule);
            }
            this.props.updateCondition(
                addCondition.type,
                addCondition.conditions
            );
        };

    deleteRule = (index) => async () => {
        const result = await getConfirmationBox({
            message: alertMessage.CONFIRM_DELETE_ITEM,
            showCloseButton: true,
            confirmButtonColor: '',
            title: alertMessage.DELETE_ITEM
        });
        if (result.value) {
            const addCondition = this.copyCondition(this.props.addCondition);
            if (addCondition.conditions) {
                addCondition.conditions.rules.splice(index, 1);
            }
            this.props.updateCondition(
                addCondition.type,
                addCondition.conditions
            );
        }
    };

    /**
     * ===================================================================================================
     */
    updateRedirectQuestion = (value) => {
        const addCondition: AddCondition<PostCondition> = this.copyCondition(
            this.props.addCondition
        ) as AddCondition<PostCondition>;
        if (addCondition.conditions) {
            addCondition.conditions.destinationQ = {
                label: value.title || value.label,
                value: value.id
            };
        }
        this.props.updateCondition(addCondition.type, addCondition.conditions);
    };

    onSaveRule = (index) => () => {
        const { addCondition } = this.props;
        this.props.updateCondition(addCondition.type, addCondition.conditions, {
            editRuleIndex: -1
        });
    };

    onAddRule = () => {
        const addCondition: AddCondition<PostCondition> = this.copyCondition(
            this.props.addCondition
        ) as AddCondition<PostCondition>;
        if (addCondition.conditions) {
            addCondition.conditions.rules.push(defaultRules());
        }
        this.props.updateCondition(addCondition.type, addCondition.conditions, {
            editRuleIndex: addCondition.conditions.rules.length - 1
        });
    };

    onEditRule = (index) => () => {
        this.props.editRule(index);
    };

    onSave = () => {
        // prevent saving when any of rules are still opened
        if (this.props.editRuleIndex >= 0) {
            this.props.toastNotification.error(
                'Please finish editing condition rules before you proceed'
            );
            return;
        }

        this.props.onSaveAddCondition();
    };

    render() {
        const { addCondition, selectConditionType: selectCondition } =
            this.props;
        const prepost =
            addCondition.type === CONDITION_TYPES.PRECONDITION ? true : false;
        return (
            <div>
                <SelectCondtionType
                    type={addCondition.type}
                    onSelect={selectCondition}
                />
                <AddPrePostCondition
                    prepost={prepost}
                    index={addCondition.index}
                    condition={addCondition.conditions}
                    errorObject={this.props.errorObject}
                    onAnswerTypeSelect={this.updateRuleOperator}
                    onCancel={this.props.onCancelCondition}
                    onAddRule={this.onAddRule}
                    onEditRule={this.onEditRule}
                    onSaveRule={this.onSaveRule}
                    onDeleteRule={this.deleteRule}
                    onDone={this.onSave}
                    onQuestionSelect={this.updateRuleReferenceQuestion}
                    onValueChange={this.updateRuleValue}
                    filteredQuestionSummaries={
                        this.props.filteredQuestionSummaries
                    }
                    flattenQuestionSummaries={
                        this.props.flattenQuestionSummaries
                    }
                    questions={this.props.questions}
                    ruleEditIndex={this.props.editRuleIndex}
                    onDestinationQuestionSelect={this.updateRedirectQuestion}
                    onCancelAddRule={this.props.onCancelAddRule}
                    editEnabled={this.props.editEnabled}
                    currentQuestionId={this.props.currentQuestionId}
                />
            </div>
        );
    }
}

export default AddConditionContainer;
